import { SxProps } from '@mui/material';
import { ClaimEventResponseItem } from 'API';
import { shortDateFormat } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { DecisionTypes, StatusTypes } from 'Enum/recognitionStatus';
import {
  DoctorOpinion,
  Examination,
  MedicalCommitte,
  RejectionStatus,
  ReqApproved,
  ReqApprovedStamp,
  ReqPaused,
  WaitingForDecision,
} from 'Images';
import { format } from 'date-fns';

export function getRecognitionStatusSteps(claimEvent: ClaimEventResponseItem) {
  const dictionary = Dictionary[DICTIONARY.Status];

  const getDecisionType = () =>
    claimEvent.claimEvents[claimEvent.claimEvents.length - 1]?.decisionType;

  const getLastStepIndex = () => {
    const decisionType = getDecisionType();
    const isGreenRout =
      claimEvent.claimEvents.find((step) => step.treatStepsGroupId === 2) ===
      undefined;

    switch (decisionType) {
      case DecisionTypes.Rejection:
      case DecisionTypes.Pause:
        return isGreenRout ? 2 : 3;

      case DecisionTypes.Recognition:
        return isGreenRout ? 4 : 5;

      default:
        let result =
          (claimEvent.claimEvents.reduce((prevStep, currStep) => {
            return (currStep.treatStepsGroupId || -1) >
              (prevStep.treatStepsGroupId || 0)
              ? currStep
              : prevStep;
          }).treatStepsGroupId || 0) - 1;
        if (result > 1 && isGreenRout) result--;

        return result;
    }
  };

  const getStepsArray = () => {
    const stepsArray = [
      dictionary.steps.step1.name,
      dictionary.steps.step2.name,
      dictionary.steps.step3.name,
      dictionary.steps.step4.name,
      dictionary.steps.step5.name,
    ].map((step, i) => {
      const endDate = claimEvent.claimEvents.find(
        (step) => step.treatStepsGroupId === i + 1
      )?.endStatusDate;
      return {
        name: step,
        endDate:
          endDate !== undefined
            ? format(
                new Date(
                  claimEvent.claimEvents.find(
                    (step) => step.treatStepsGroupId === i + 1
                  )!.endStatusDate!
                ),
                shortDateFormat
              )
            : '',
      };
    });

    const isGreenRout =
      claimEvent.claimEvents.find((step) => step.treatStepsGroupId === 2) ===
      undefined;
    const decisionType = getDecisionType();

    switch (decisionType) {
      case DecisionTypes.Rejection:
        return [
          ...(isGreenRout
            ? [stepsArray[0], stepsArray[2]]
            : stepsArray?.slice(0, 3)),
          { name: dictionary.steps.rejection.name },
        ];

      case DecisionTypes.Pause:
        return [
          ...(isGreenRout
            ? [stepsArray[0], stepsArray[2]]
            : stepsArray?.slice(0, 3)),
          { name: dictionary.steps.pause.name },
        ];

      case DecisionTypes.Recognition:
        return [
          ...(isGreenRout
            ? [stepsArray[0], ...stepsArray?.slice(2)]
            : stepsArray),
          { name: dictionary.steps.recognition.name },
        ];

      default:
        const lastStepIndex = getLastStepIndex();
        let newSteps = [...stepsArray];
        if (isGreenRout) newSteps = [stepsArray[0], ...stepsArray?.slice(2)];
        if (newSteps[lastStepIndex]?.name === dictionary.steps.step3.name)
          newSteps[lastStepIndex].name = dictionary.steps.step3.nameIfCurrent;

        return newSteps;
    }
  };

  return getStepsArray();
}

export function getLastStepIndex(claimEvent: ClaimEventResponseItem) {
  const getDecisionType = () =>
    claimEvent.claimEvents[claimEvent.claimEvents.length - 1]?.decisionType;

  const getLastStepIndex = () => {
    const decisionType = getDecisionType();
    const isGreenRout =
      claimEvent.claimEvents.find((step) => step.treatStepsGroupId === 2) ===
      undefined;

    switch (decisionType) {
      case DecisionTypes.Rejection:
      case DecisionTypes.Pause:
        return isGreenRout ? 2 : 3;

      case DecisionTypes.Recognition:
        return isGreenRout ? 4 : 5;

      default:
        let result =
          (claimEvent.claimEvents.reduce((prevStep, currStep) => {
            return (currStep.treatStepsGroupId || -1) >
              (prevStep.treatStepsGroupId || 0)
              ? currStep
              : prevStep;
          }).treatStepsGroupId || 0) - 1;
        if (result > 1 && isGreenRout) result--;

        return result;
    }
  };

  return getLastStepIndex();
}

export function getLabelStyle(
  stepId: number | undefined,
  decisionType: number | undefined
) {
  const labelStyle: SxProps = { bgcolor: '', border: '' };
  if (
    decisionType === DecisionTypes.InProcess ||
    decisionType === DecisionTypes.FurtherReference
  ) {
    if (stepId === StatusTypes.REQUEST_EXAMINATION) {
      labelStyle.bgcolor = '#E7EDFF';
      labelStyle.border = '1px solid #B7CCFF';
    } else if (stepId === StatusTypes.MEDICAL_OPINION) {
      labelStyle.bgcolor = '#FFF0ED';
      labelStyle.border = '1px solid #FFDCD4';
    } else if (stepId === StatusTypes.WAITING_FOR_DECISION) {
      labelStyle.bgcolor = '#E7EDFF';
      labelStyle.border = '1px solid #B7CCFF';
    } else if (stepId === StatusTypes.MEDICAL_COMMITTEE) {
      labelStyle.bgcolor = '#FFF7EA';
      labelStyle.border = '1px solid #FFF7EA';
    } else if (stepId === StatusTypes.DISABILITY_RECOGNITION) {
      labelStyle.bgcolor = '#E0F6EF';
      labelStyle.border = '1px solid #B8EAD9';
    } else {
      labelStyle.bgcolor = '#E0F6EF';
      labelStyle.border = '1px solid #B8EAD9';
    }
  } else if (decisionType === DecisionTypes.Rejection) {
    labelStyle.bgcolor = '#FBE6E6';
    labelStyle.border = '1px solid #F7C4C4';
  } else if (decisionType === DecisionTypes.Pause) {
    labelStyle.bgcolor = '#F4E4FF';
    labelStyle.border = '1px solid #E4C1FF';
  } else if (decisionType === DecisionTypes.Recognition) {
    labelStyle.bgcolor = '#E0F6EF';
    labelStyle.border = '1px solid #B8EAD9';
  } else if (decisionType === DecisionTypes.Objection) {
    labelStyle.bgcolor = '#F4E4FF';
    labelStyle.border = '1px solid #E4C1FF';
  }

  return labelStyle;
}

export function getStepIcon(
  stepId: number | undefined,
  decisionType: number | undefined
) {
  if (
    decisionType === DecisionTypes.InProcess ||
    decisionType === DecisionTypes.FurtherReference
  ) {
    if (stepId === StatusTypes.REQUEST_EXAMINATION) {
      return Examination;
    } else if (stepId === StatusTypes.MEDICAL_OPINION) {
      return DoctorOpinion;
    } else if (stepId === StatusTypes.WAITING_FOR_DECISION) {
      return WaitingForDecision;
    } else if (stepId === StatusTypes.MEDICAL_COMMITTEE) {
      return MedicalCommitte;
    } else if (stepId === StatusTypes.DISABILITY_RECOGNITION) {
      return ReqApprovedStamp;
    } else {
      return '';
    }
  } else if (decisionType === DecisionTypes.Rejection) {
    return RejectionStatus;
  } else if (decisionType === DecisionTypes.Pause) {
    return ReqPaused;
  } else if (decisionType === DecisionTypes.Recognition) {
    return ReqApproved;
  } else if (decisionType === DecisionTypes.Objection) {
    return RejectionStatus;
  } else return '';
}
